@if (isLoading() && !grid()) {
  <div class="py-6 flex justify-center">
    <mat-spinner diameter="32" />
  </div>
} @else if (!hasPriceLists()) {
  <!-- Nothing can be priced without a price list, and the lake's Price lists section is a page away, so
       the way out is offered right here. -->
  <div class="py-4">
    <p class="text-gray-500">{{ 'waters.combination-prices.no-price-lists' | translate }}</p>
    <app-add-price-list-button
      class="inline-block mt-2"
      [waterId]="waterId()"
      (created)="onPriceListCreated($event)" />
  </div>
} @else {
  <!-- Dimension selectors: price list + season (dropdowns), stay type (tabs below) -->
  <div class="flex flex-wrap gap-4 items-center mb-2">
    <div class="flex items-center gap-2">
      <app-price-list-select
        [priceLists]="priceLists()"
        [selectedId]="selectedPriceListId()"
        [label]="'waters.combination-prices.price-list' | translate"
        [discardTitle]="'waters.combination-prices.discard-title' | translate"
        [discardPrompt]="'waters.combination-prices.discard-prompt' | translate"
        [hasUnsavedChanges]="dirty()"
        (priceListChange)="loadGrid($event)" />

      <app-add-price-list-button
        [compact]="true"
        [waterId]="waterId()"
        [hasUnsavedChanges]="dirty()"
        [disabled]="isLoading()"
        (created)="onPriceListCreated($event)" />
    </div>

    @if (hasSeasons()) {
      <mat-form-field subscriptSizing="dynamic" class="min-w-48">
        <mat-label>{{ 'waters.combination-prices.season' | translate }}</mat-label>
        <mat-select [value]="activeSeasonId()" (selectionChange)="onSeasonChange($event.value)">
          @for (season of seasonOptions(); track season.id) {
            <mat-option [value]="season.id">{{ season.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    }

    @if (canRenderGrid() && copySources().length > 0) {
      <button mat-stroked-button class="rect-btn" [matMenuTriggerFor]="copyMenu">
        <mat-icon>content_copy</mat-icon>
        {{ 'waters.combination-prices.copy-from' | translate }}
      </button>
      <mat-menu #copyMenu="matMenu">
        @for (source of copySources(); track source.type + ':' + source.id) {
          <button mat-menu-item (click)="copyFrom(source)">{{ source.label }}</button>
        }
      </mat-menu>
    }

    @if (isLoading()) {
      <mat-spinner diameter="24" />
    }

    @if (canRenderGrid()) {
      <button mat-button color="primary" class="ml-auto" (click)="resetVisibleGrid()">
        {{ 'waters.combination-prices.reset-grid' | translate }}
      </button>
    }
  </div>

  @if (capacityMissing()) {
    <p class="text-gray-500 py-4">{{ 'waters.combination-prices.capacity-missing' | translate }}</p>
  } @else if (!hasSeasons()) {
    <p class="text-gray-500 py-4">{{ 'waters.combination-prices.no-seasons' | translate }}</p>
  } @else if (!hasStayTypes()) {
    <p class="text-gray-500 py-4">{{ 'waters.combination-prices.no-stay-types' | translate }}</p>
  } @else if (minAnglersExceedsSpots()) {
    <p class="text-gray-500 py-4">{{ 'waters.combination-prices.min-exceeds-spots' | translate }}</p>
  } @else {
    <mat-tab-group
      animationDuration="0ms"
      [selectedIndex]="activeStayTypeIndex()"
      (selectedIndexChange)="onStayTypeTabChange($event)">
      @for (stayType of stayTypes(); track stayType.id) {
        <mat-tab [label]="stayType.name" />
      }
    </mat-tab-group>

    @if (showErrors() && hasPartialCells()) {
      <p class="text-red-600 text-sm mt-3">{{ 'waters.combination-prices.both-prices-required' | translate }}</p>
    }
    @if (showErrors() && hasOutOfRangeCells()) {
      <p class="text-red-600 text-sm mt-3">{{ 'waters.combination-prices.price-out-of-range' | translate }}</p>
    }

    @if (canRenderGrid()) {
      <div class="overflow-x-auto mt-4">
        <table class="combi-grid">
          <thead>
            <tr>
              <th class="corner">{{ 'waters.combination-prices.anglers-nonanglers' | translate }}</th>
              @for (nonAnglers of nonAnglersRange(); track nonAnglers) {
                <th>{{ nonAnglers }}</th>
              }
            </tr>
          </thead>
          <tbody>
            @for (anglers of anglersRange(); track anglers) {
              <tr>
                <th class="row-head">{{ anglers }}</th>
                @for (nonAnglers of nonAnglersRange(); track nonAnglers) {
                  <td [class.cell-invalid]="showErrors() && isCellInvalid(anglers, nonAnglers)">
                    <mat-form-field subscriptSizing="dynamic" floatLabel="always" class="w-full">
                      <mat-label>{{ 'waters.combination-prices.purchase' | translate }}</mat-label>
                      <input
                        matInput
                        type="number"
                        min="0"
                        step="0.01"
                        [value]="getPurchase(anglers, nonAnglers)"
                        (input)="setPurchase(anglers, nonAnglers, $event)" />
                      <span matTextPrefix>€&nbsp;</span>
                    </mat-form-field>
                    <mat-form-field subscriptSizing="dynamic" floatLabel="always" class="w-full mt-3">
                      <mat-label>{{ 'waters.combination-prices.sale' | translate }}</mat-label>
                      <input
                        matInput
                        type="number"
                        min="0"
                        step="0.01"
                        [value]="getSelling(anglers, nonAnglers)"
                        (input)="setSelling(anglers, nonAnglers, $event)" />
                      <span matTextPrefix>€&nbsp;</span>
                    </mat-form-field>
                    @if (showErrors() && isCellPartial(anglers, nonAnglers)) {
                      <div class="mt-1 text-xs text-right text-red-600">{{ 'waters.combination-prices.both-prices-required-cell' | translate }}</div>
                    } @else if (showErrors() && isCellInvalid(anglers, nonAnglers)) {
                      <div class="mt-1 text-xs text-right text-red-600">{{ 'waters.combination-prices.price-out-of-range-cell' | translate }}</div>
                    } @else {
                      @let m = margin(anglers, nonAnglers);
                      <div class="mt-1 text-xs text-right text-gray-500">
                        @if (m !== null) {
                          {{ m | number: '1.1-1' }}% margin
                        } @else {
                          &mdash;
                        }
                      </div>
                    }
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  }
}
